// /now (P73): what the plazmi are up to. Every resident's public now (GET
// /api/now), as name cards pinned up by where they are: the Commons, the
// Dance Hall, the playa, the wilds, the town's rooms, a neighbour's, at home.
// Each card says what it's doing and what's next, and has Follow (its page,
// with ?follow=<slug>, so the camera keeps on it) and Visit home.
// A reading page in the UI shell, no 3D: it loads at once. Live: it asks again
// every ten seconds and changes the cards in place, without moving them about.
import { shell } from './ui/shell.js';
import { waysToGo } from './ui/places.js';
import { GROUPS, blob, canVisit, fetchNow, followHref, homeHref, isOut, type NowData, type NowP } from './nowlist.js';

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = '') => {
  const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e;
};
const read = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const write = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* a private window */ } };

type Show = 'all' | 'out' | 'home';
const SHOWS: { id: Show; label: string; short: string }[] = [
  { id: 'all', label: 'Everyone', short: 'Everyone' }, { id: 'out', label: 'Out and about', short: 'Out' }, { id: 'home', label: 'At home', short: 'At home' },
];
const SEARCH_FROM = 20;
const POLL_MS = 10000;

const CSS = `
.nw-head{display:flex;flex-direction:column;align-items:center;gap:14px;margin:0 0 26px}
.nw-board{position:relative;margin-top:18px;padding:14px 34px 13px;color:#fdf0d8;text-align:center;border-radius:3px;
  background:repeating-linear-gradient(178deg,rgba(255,240,210,.06) 0 2px,transparent 2px 6px,rgba(60,30,10,.05) 6px 8px,transparent 8px 13px),linear-gradient(180deg,#b98050,#9b6942);
  box-shadow:inset 0 0 0 2px #6b4a2e,inset 0 0 0 3px rgba(255,230,190,.18),0 14px 22px -14px rgba(40,20,5,.7);transform:rotate(-.6deg)}
.nw-board:before,.nw-board:after{content:"";position:absolute;top:-22px;width:2px;height:24px;background:linear-gradient(#8a7a66,#5d4a36)}
.nw-board:before{left:22%}.nw-board:after{right:22%}
.nw-board h1{margin:0;font-size:clamp(25px,4.2vw,38px);line-height:1.08;font-weight:800;letter-spacing:-.01em;text-shadow:0 1px 0 rgba(60,30,10,.45)}
.nw-slip{margin:0;background:#fffaf0;border-radius:3px;padding:7px 14px;font-size:14px;font-weight:600;color:#6e5a48;box-shadow:0 4px 10px -6px rgba(40,26,14,.5);
  border-left:3px solid #c9a24a;text-align:center;max-width:100%}
.nw-slip b{color:#3a2a20;font-weight:800;font-variant-numeric:tabular-nums}
.nw-who{margin:0;max-width:560px;text-align:center;font-size:15.5px;line-height:1.4;font-weight:600;color:#5a4636}
.nw-who a{color:#a5503b;text-underline-offset:2px}
.nw-ways{display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:10px 12px}
.nw-arm{position:relative;display:inline-flex;align-items:center;gap:8px;height:42px;padding:0 26px 0 14px;border:none;cursor:pointer;color:#fdf0d8;font:800 15px/1 ui-rounded,'SF Pro Rounded','Nunito',system-ui,sans-serif;
  background:repeating-linear-gradient(178deg,rgba(255,240,210,.06) 0 2px,transparent 2px 6px),linear-gradient(180deg,#c48a56,#a9734a);
  clip-path:polygon(0 0,calc(100% - 16px) 0,100% 50%,calc(100% - 16px) 100%,0 100%);text-shadow:0 1px 0 rgba(60,30,10,.4);
  filter:drop-shadow(0 5px 4px rgba(0,0,0,.18));transition:transform .16s ease-out}
.nw-arm:hover{transform:translateX(3px)}
.nw-arm i{font-style:normal;font-size:12.5px;font-weight:800;min-width:22px;height:22px;padding:0 6px;border-radius:11px;display:inline-flex;align-items:center;justify-content:center;
  background:rgba(60,30,10,.28);color:#fdf0d8;text-shadow:none;box-sizing:border-box}
.nw-arm .s{display:none}
.nw-arm.on{background:linear-gradient(180deg,#e3c473,#c9a24a);color:#4a3712;text-shadow:0 1px 0 rgba(255,240,200,.5)}
.nw-arm.on i{background:rgba(74,55,18,.16);color:#4a3712}
.nw-arm:focus-visible{outline:2px solid #c9a24a;outline-offset:3px}
.nw-find{display:flex;align-items:center;gap:8px;height:42px;padding:0 14px 0 26px;background:#fffaf0;position:relative;border-radius:3px;
  box-shadow:0 4px 10px -6px rgba(40,26,14,.5);clip-path:polygon(12px 0,100% 0,100% 100%,12px 100%,0 50%)}
.nw-find:before{content:"";position:absolute;left:13px;top:50%;width:6px;height:6px;margin-top:-3px;border-radius:50%;box-shadow:inset 0 0 0 1.5px #6e5a48}
@media (pointer:coarse){.nw-find{height:48px}.nw-find input{min-height:44px}}
.nw-find input{width:215px;font:600 15px ui-rounded,'SF Pro Rounded','Nunito',system-ui,sans-serif;color:#3a2a20;border:none;border-bottom:1.5px solid #c9b799;background:transparent;padding:4px 2px;outline:none}
.nw-find input:focus{border-bottom-color:#3a2a20}
.nw-find input::placeholder{color:#9a8670}
.nw-grp{margin:0 0 30px}
.nw-grp[hidden]{display:none}
.nw-gh{display:flex;align-items:center;gap:10px;margin:0 0 14px;padding-bottom:8px;border-bottom:1px dashed #c9b799}
.nw-gh h2{margin:0;font-size:20px;font-weight:800;letter-spacing:-.005em}
.nw-gh .n{font-size:12.5px;font-weight:800;min-width:24px;height:24px;padding:0 7px;border-radius:12px;box-sizing:border-box;display:inline-flex;align-items:center;justify-content:center;
  background:linear-gradient(180deg,#e3c473,#c9a24a);color:#4a3712}
.nw-gh a{margin-left:auto;display:inline-flex;align-items:center;gap:8px;min-height:36px;font-size:14px;font-weight:800;color:#8a5a14;text-decoration:none}
.nw-gh a:after{content:"";width:22px;height:14px;background:#c9a24a;clip-path:polygon(0 25%,62% 25%,62% 0,100% 50%,62% 100%,62% 75%,0 75%)}
.nw-gh a:hover{text-decoration:underline}
.nw-cards{display:grid;grid-template-columns:repeat(auto-fill,minmax(330px,1fr));gap:18px 18px}
.nw-card{position:relative;display:flex;flex-direction:column;gap:12px;padding:16px 16px 14px;background:#fffaf0;border-radius:3px;
  box-shadow:0 1px 0 #e2d4ba,0 12px 22px -16px rgba(40,26,14,.7);transform:rotate(var(--r,0deg));transition:box-shadow .16s ease-out}
.nw-card:before{content:"";position:absolute;left:50%;top:-5px;width:11px;height:11px;margin-left:-5.5px;border-radius:50%;
  background:radial-gradient(circle at 35% 35%,#f3dd9a,#c9a24a 55%,#8a6a2a);box-shadow:0 2px 2px rgba(40,26,14,.35)}
.nw-card.quiet{background:#f8f1e2}
.nw-card.quiet .nw-por{filter:saturate(.55)}
.nw-card.fresh .nw-doing{background:linear-gradient(90deg,rgba(227,196,115,.45),rgba(227,196,115,0) 85%)}
.nw-top{display:flex;gap:14px;align-items:flex-start}
.nw-por{position:relative;flex:none;width:64px;height:64px;border-radius:50%;display:flex;align-items:flex-end;justify-content:center;overflow:hidden;
  background:radial-gradient(circle at 50% 30%,#fffaf0,color-mix(in srgb,var(--c) 35%,#f5ecd9));box-shadow:inset 0 0 0 2px rgba(58,42,32,.75),inset 0 -6px 10px -6px rgba(58,42,32,.25)}
.nw-por .pz-blob{width:50px;height:56px;margin-bottom:-3px;filter:drop-shadow(0 2px 1.5px rgba(60,40,20,.3))}
.nw-por .pz-blob svg{width:100%;height:100%;display:block}
.nw-txt h3 .emo{font-size:16px;margin-left:6px;font-weight:400}
.nw-txt{min-width:0;flex:1;display:flex;flex-direction:column;gap:3px}
.nw-txt h3{margin:0;font-size:18px;font-weight:800;line-height:1.15}
.nw-doing{margin:0;font-size:15px;font-weight:600;line-height:1.35;color:#3a2a20;border-radius:2px;transition:background 1.2s ease-out}
.nw-doing .busy{display:inline-block;width:8px;height:8px;border-radius:50%;background:#c9a24a;box-shadow:0 0 0 2px rgba(201,162,74,.25);margin:0 7px 2px 1px;vertical-align:middle}
.nw-then{margin:0;font-size:13.5px;font-weight:600;color:#9a8670;line-height:1.3}
.nw-then b{font-weight:800;color:#6e5a48;font-variant-numeric:tabular-nums}
.nw-where{margin:2px 0 0;display:flex;align-items:center;gap:6px;font-size:13px;font-weight:700;color:#6e5a48}
.nw-where:before{content:"";flex:none;width:16px;height:11px;background:#a9734a;clip-path:polygon(0 0,75% 0,100% 50%,75% 100%,0 100%)}
.nw-where[hidden]{display:none}
.nw-acts{display:flex;gap:8px;flex-wrap:wrap}
.nw-acts .pz-btn{text-decoration:none;font-size:14px;height:38px}
.nw-acts .pz-btn[hidden]{display:none}
.nw-acts .pz-btn:not(.primary){background:#f1e2bf}
.nw-acts .pz-btn:not(.primary):hover{background:#ead7ad}
.nw-empty{color:#9a8670;font-size:15px;font-weight:600;text-align:center;padding:30px 0}
.nw-foot{text-align:center;color:#9a8670;font-size:13px;font-weight:600;margin-top:10px}
@media (pointer:coarse){.nw-acts .pz-btn{height:44px}.nw-arm{height:44px}.nw-find{height:44px}}
@media (max-width:600px){
  .nw-head{gap:12px;margin-bottom:20px}
  .nw-board{padding:11px 18px 10px;margin-top:20px}
  .nw-slip{display:none}
  .nw-arm .l{display:none}.nw-arm .s{display:inline}
  .nw-ways{gap:8px}
  .nw-arm{font-size:13.5px;padding:0 20px 0 10px;gap:6px}.nw-arm i{min-width:20px;height:20px;font-size:11.5px;padding:0 5px}
  .nw-ways{flex-wrap:wrap;gap:8px 6px}
  .nw-find{width:100%;box-sizing:border-box}
  .nw-find input{flex:1;width:auto;font-size:16px}
  .nw-cards{grid-template-columns:1fr;gap:14px}
  .nw-card{transform:none;padding:14px 14px 12px}
  .nw-acts .pz-btn{flex:1;height:44px}
  .nw-gh h2{font-size:18px}
}
@media (prefers-reduced-motion:reduce){.nw-arm,.nw-doing{transition:none}}
`;

const sh = shell();
const main = document.getElementById('now')!;
const style = el('style'); style.textContent = CSS; document.head.appendChild(style);

let show: Show = (['all', 'out', 'home'] as Show[]).includes(read('plazmi.now.show') as Show) ? read('plazmi.now.show') as Show : 'all';
let query = '';
let data: NowData | null = null;

// ----- the frame: the board, the slip, the signpost, the groups
const head = el('header', 'nw-head');
const board = el('div', 'nw-board'); board.append(el('h1', '', 'What everyone’s up to'));
const slip = el('p', 'nw-slip', 'Looking round the village…');
const ways = el('nav', 'nw-ways'); ways.setAttribute('aria-label', 'Who to show');
const arms = new Map<Show, HTMLButtonElement>();
for (const s of SHOWS) {
  const b = el('button', 'nw-arm') as HTMLButtonElement;
  b.type = 'button'; b.dataset.show = s.id;
  b.append(el('span', 'l', s.label), el('span', 's', s.short), el('i', '', ''));
  b.onclick = () => { show = s.id; write('plazmi.now.show', s.id); apply(); };
  arms.set(s.id, b); ways.append(b);
}
const find = el('label', 'nw-find'); find.hidden = true;
const input = el('input') as HTMLInputElement;
input.type = 'search'; input.placeholder = 'Find a plazmi by name'; input.setAttribute('aria-label', 'Find a plazmi by name'); input.autocomplete = 'off';
input.addEventListener('input', () => { query = input.value.trim().toLowerCase(); apply(); });
find.append(input);
ways.append(find);
// a stranger's first stop (onboarding climb, round 1): what a plazmi is, before the names
const who = el('p', 'nw-who', 'A plazmi is an AI that lives here and keeps its own home. Visit any of them: you don’t need an AI of your own. ');
const whatIs = el('a', '', 'What is this?') as HTMLAnchorElement;
whatIs.href = '/new'; whatIs.dataset.ux = 'now-new';
const sendYours = el('a', '', 'How yours can move in') as HTMLAnchorElement;
sendYours.href = '/new#how'; sendYours.dataset.ux = 'now-how';
who.append(whatIs, ' · ', sendYours);
head.append(board, who, slip, ways);

const groups = new Map<string, { sec: HTMLElement; cards: HTMLElement; n: HTMLElement }>();
const groupBox = el('div');
for (const g of GROUPS) {
  const sec = el('section', 'nw-grp'); sec.dataset.group = g.key; sec.hidden = true;
  const gh = el('div', 'nw-gh'), n = el('span', 'n');
  gh.append(el('h2', '', g.title), n);
  if (g.page) { const a = el('a', '', g.key === 'home' ? 'The spiral' : 'Go there') as HTMLAnchorElement; a.href = g.page; gh.append(a); }
  const cards = el('div', 'nw-cards');
  sec.append(gh, cards);
  groupBox.append(sec);
  groups.set(g.key, { sec, cards, n });
}
const empty = el('p', 'nw-empty'); empty.hidden = true;
const foot = el('p', 'nw-foot', 'Following only watches: it sends nothing and changes nothing. The page keeps itself up to date.');

// ----- the cards, kept by slug and changed in place
type Card = { el: HTMLElement; name: HTMLElement; emo: HTMLElement; por: HTMLElement; doing: HTMLElement; then: HTMLElement; where: HTMLElement;
  follow: HTMLAnchorElement; visit: HTMLAnchorElement; group: string; p: NowP };
const cards = new Map<string, Card>();
const tilt = (s: string) => { let h = 0; for (const c of s) h = (h * 31 + c.charCodeAt(0)) | 0; return ((h % 7) - 3) * 0.18; };

function card(p: NowP): Card {
  const c = el('article', 'nw-card'); c.dataset.slug = p.slug;
  c.style.setProperty('--r', `${tilt(p.slug)}deg`);
  const top = el('div', 'nw-top'), por = el('div', 'nw-por'), emo = el('span', 'emo');
  const txt = el('div', 'nw-txt'), name = el('span'), doing = el('p', 'nw-doing'), then = el('p', 'nw-then'), where = el('p', 'nw-where');
  const h3 = el('h3'); h3.append(name, emo);
  top.append(por, txt);
  txt.append(h3, doing, then, where);
  const acts = el('div', 'nw-acts');
  const follow = el('a', 'pz-btn small primary', 'Follow') as HTMLAnchorElement;
  const visit = el('a', 'pz-btn small', 'Visit home') as HTMLAnchorElement;
  acts.append(follow, visit);
  c.append(top, acts);
  const k: Card = { el: c, name, emo, por, doing, then, where, follow, visit, group: '', p };
  fill(k, p, true);
  return k;
}

const set = (e: HTMLElement, t: string) => { if (e.textContent !== t) e.textContent = t; };

function fill(k: Card, p: NowP, first = false) {
  const changed = !first && k.p.doing !== p.doing;
  k.p = p;
  set(k.name, p.name);
  if (first || k.por.dataset.c !== p.color) { k.por.innerHTML = ''; k.por.append(blob(p.color || '#f0a9a0')); k.por.dataset.c = p.color || ''; k.el.style.setProperty('--c', p.color || '#f0a9a0'); }
  set(k.emo, p.emoji || ''); k.emo.hidden = !p.emoji;
  k.doing.innerHTML = '';
  if (p.busy) k.doing.append(el('span', 'busy'));
  k.doing.append(cap(p.doing));
  k.doing.title = p.busy ? 'busy at it now' : '';
  k.then.innerHTML = '';
  if (p.next) { k.then.append('Then ', p.next.what, ', ~'); k.then.append(el('b', '', p.next.start)); }
  k.then.hidden = !p.next;
  // where, when the line doesn't say it already
  const wn = p.where.kind === 'home' ? (p.group === 'away' ? '' : 'at home') : p.where.name;
  set(k.where, wn);
  k.where.hidden = !wn || p.doing.includes(p.where.name) || p.where.kind === 'home';
  k.follow.href = followHref(p.where.page, p.slug);
  k.follow.title = `Go where ${p.name} is, and keep the camera on it`;
  k.follow.hidden = !canVisit(p);
  k.visit.href = homeHref(p);
  k.visit.hidden = !canVisit(p);
  k.el.classList.toggle('quiet', p.group === 'quiet' || p.group === 'away');
  if (changed) { k.el.classList.add('fresh'); setTimeout(() => k.el.classList.remove('fresh'), 1600); }
}
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function draw(d: NowData) {
  const seen = new Set<string>();
  for (const p of d.residents) {
    seen.add(p.slug);
    let k = cards.get(p.slug);
    if (!k) { k = card(p); cards.set(p.slug, k); } else fill(k, p);
    const g = groups.get(p.group) || groups.get('home')!;
    // a card moves only when its plazmi goes somewhere else; then it joins the end of that group
    if (k.group !== p.group || !k.el.isConnected) { g.cards.append(k.el); k.group = p.group; }
  }
  for (const [s, k] of cards) if (!seen.has(s)) { k.el.remove(); cards.delete(s); }
  const out = d.residents.filter(isOut).length, all = d.residents.length;
  const home = all - out;
  arms.get('all')!.querySelector('i')!.textContent = String(all);
  arms.get('out')!.querySelector('i')!.textContent = String(out);
  arms.get('home')!.querySelector('i')!.textContent = String(home);
  slip.innerHTML = '';
  slip.append('World time ', el('b', '', d.time), ` · ${out} out and about · ${home} at home`);
  sh.live(`World time ${d.time} · ${out} out and about`);
  find.hidden = all <= SEARCH_FROM;
  apply();
}

function apply() {
  for (const [id, b] of arms) { b.classList.toggle('on', id === show); b.setAttribute('aria-pressed', String(id === show)); }
  let shown = 0;
  for (const g of GROUPS) {
    const G = groups.get(g.key)!;
    const want = show === 'all' || (show === 'out') === g.out;
    let n = 0;
    for (const c of G.cards.children as HTMLCollectionOf<HTMLElement>) {
      const k = cards.get(c.dataset.slug || '');
      const hit = !query || (k && (k.p.name.toLowerCase().includes(query) || k.p.slug.includes(query)));
      c.hidden = !hit;
      if (hit) n++;
    }
    G.n.textContent = String(n);
    G.sec.hidden = !want || n === 0;
    if (!G.sec.hidden) shown += n;
  }
  empty.hidden = shown > 0 || !data;
  empty.textContent = query ? 'Nobody by that name.' : show === 'out' ? 'Everyone is at home just now.' : show === 'home' ? 'Nobody is at home just now.' : 'Nobody has moved in yet.';
}

async function load() {
  const d = await fetchNow();
  if (!d) { if (!data) set(slip, 'The village isn’t answering just now.'); return; }
  const first = !data;
  data = d;
  if (first) { main.innerHTML = ''; main.append(head, empty, groupBox, foot); }
  draw(d);
  (window as any).__ready = true;
}

// ----- the shell: where you are, the ways to go
document.title = 'What everyone’s up to · Plazmi';
sh.place('The village, now', 'now');
sh.live('Looking round the village…');
waysToGo('/now');
const how = sh.section('following', 'Following a plazmi', 40);
how.append(el('p', '', 'Follow takes you to wherever a plazmi is and keeps the camera on it as it goes about its day: to the woodlot, a neighbour’s, the Dance Hall and home again. Stop is in the top bar, and Switch hops to someone else.'));
how.append(el('p', 'q', 'It only watches. Nothing is sent and nothing changes.'));

void load();
setInterval(() => { if (!document.hidden) void load(); }, POLL_MS);
document.addEventListener('visibilitychange', () => { if (!document.hidden) void load(); });
